import Countdown, { zeroPad } from 'react-countdown'
import { useFormContext, useWatch } from 'react-hook-form'

import { FormattedText } from '~/components/FormattedText'
import { PieTimer } from '~/components/PieTimer'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { TransactionDetailsPreview } from '~/components/smart-accounts/TransactionDetailsPreview'
import { BaseTransactionFormValues } from '~/components/smart-accounts/schemas'
import { PendingStateChangeDisplay } from '~/components/txn-display/state-change/components/PendingStateChangeDisplay'
import { OfframpDisplayData } from '~/components/txn-display/state-change/types'
import { StateChange } from '~/components/txn-display/state-change/types'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { BackendTransactionDecodedCall, BasicTransactionType } from '~/types'
import { IAddress } from '~/types'

/**
 * Component to display the review page of a transaction.
 *
 * @param smartAccountAddress - The address of the smart account
 * @param chainId - The chain ID of the transaction
 * @param calls - The calls of the transaction
 * @param addressesToWatch - The addresses to watch for state changes
 * @param bridgeStateChanges - The bridge state changes
 * @param refreshData - If the form has a background refresh aspect, it should pass the date it was last updated and the refetch interval.
 *                      This is used to display a countdown to the next refresh.
 * @param stateChangesToHide - The state changes to hide from the state change list
 * @param transactionTypes - The transaction types
 */
export const TransactionReview = ({
  smartAccountAddress,
  chainId,
  calls,
  addressesToWatch,
  bridgeStateChanges,
  refreshData,
  stateChangesToHide,
  transactionTypes,
  offrampData,
}: {
  smartAccountAddress: IAddress
  chainId: number
  calls: BackendTransactionDecodedCall[]
  addressesToWatch: IAddress[]
  bridgeStateChanges?: StateChange[]
  refreshData?: {
    dataUpdatedAt: number
    refetchInterval: number
    isLoading: boolean
  }
  stateChangesToHide?: Record<IAddress, Record<IAddress, boolean>>
  transactionTypes: BasicTransactionType[]
  offrampData?: OfframpDisplayData
}) => {
  const { control } = useFormContext<BaseTransactionFormValues>()

  const memo = useWatch({ control, name: 'memo' })

  const { smartAccounts } = useSmartAccounts()
  const smartAccount = smartAccounts.find(
    (smartAccount) => smartAccount.address === smartAccountAddress,
  )

  return (
    <>
      {memo && memo.length > 0 && (
        <p className="max-w-full text-primary">
          <FormattedText text={memo} />
        </p>
      )}
      <div className="flex flex-col space-y-1.5">
        <PendingStateChangeDisplay
          txnData={{
            smartAccountAddress,
            chainId,
            calls,
            addressesToWatch,
          }}
          bridgeStateChanges={bridgeStateChanges}
          dataRefreshing={refreshData?.isLoading}
          stateChangesToHide={stateChangesToHide}
          offrampData={offrampData}
        />
        {refreshData && (
          <div className="flex justify-items-start">
            <Countdown
              key={refreshData.dataUpdatedAt + refreshData.refetchInterval}
              date={refreshData.dataUpdatedAt + refreshData.refetchInterval}
              renderer={({ minutes, seconds, completed, total }) => {
                const totalTime = refreshData.refetchInterval
                const timeRemaining = Math.max(0, total)
                const percentage = Math.min(
                  100,
                  Math.max(0, ((totalTime - timeRemaining) / totalTime) * 100),
                )
                const isRefreshingQuote = completed || refreshData.isLoading

                return (
                  <div className="flex items-center gap-2 leading-none">
                    {isRefreshingQuote ? (
                      <div className="flex items-center justify-center p-px text-secondary">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : (
                      <PieTimer
                        active={true}
                        endColor="#3B82F6"
                        startColor="#3B82F650"
                        percentage={percentage}
                      />
                    )}
                    {isRefreshingQuote ? (
                      <p className="flex items-center gap-1 text-xs text-secondary">
                        Refreshing quote...
                      </p>
                    ) : (
                      <p className="text-xs text-secondary">
                        Refreshing quote in{' '}
                        {`${zeroPad(minutes)}:${zeroPad(seconds)}`}
                      </p>
                    )}
                  </div>
                )
              }}
            />
          </div>
        )}
      </div>
      {smartAccount?.id && (
        <TransactionDetailsPreview
          chainId={chainId}
          calls={calls}
          smartAccountId={smartAccount.id}
          transactionTypes={transactionTypes}
          showInsufficientGasWarning={true}
        />
      )}
    </>
  )
}
